import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Implements the WAI-ARIA Grid Pattern for the month calendar.
// Roving tabindex: exactly one cell is tabbable at a time. Arrow keys
// move focus; PageUp/PageDown navigate between months via Turbo.
export default class extends Controller {
  static targets = ["cell"]
  static values  = { prevUrl: String, nextUrl: String }

  connect() {
    this.element.addEventListener("keydown", this.handleKeydown.bind(this))
  }

  handleKeydown(event) {
    const cell = event.target.closest("[data-calendar-target=cell]")
    if (!cell) return

    const date = this.parseDate(cell.dataset.date)
    if (!date) return

    const onLink = event.target.tagName === "A"

    switch (event.key) {
      case "ArrowLeft":  event.preventDefault(); this.moveByDays(date, -1);  break
      case "ArrowRight": event.preventDefault(); this.moveByDays(date,  1);  break
      case "ArrowUp":
        event.preventDefault()
        if (onLink && this.moveLinkFocus(cell, event.target, -1)) break
        this.moveByDays(date, -7)
        break
      case "ArrowDown":
        event.preventDefault()
        if (onLink && this.moveLinkFocus(cell, event.target, 1)) break
        this.moveByDays(date, 7)
        break
      case "Home":       event.preventDefault(); this.moveToWeekEdge(date, "start"); break
      case "End":        event.preventDefault(); this.moveToWeekEdge(date, "end");   break
      case "PageUp":     event.preventDefault(); this.visit(this.prevUrlValue); break
      case "PageDown":   event.preventDefault(); this.visit(this.nextUrlValue); break
      case "Escape":
        if (onLink) { event.preventDefault(); cell.focus() }
        break
      case "Enter":
      case " ":
        if (event.target === cell) this.activateCell(cell, event)
        break
    }
  }

  moveLinkFocus(cell, currentLink, direction) {
    const links = Array.from(cell.querySelectorAll("a[href]"))
    const next = links[links.indexOf(currentLink) + direction]
    if (!next) return false
    next.focus()
    return true
  }

  activateCell(cell, event) {
    const links = cell.querySelectorAll("a[href]")
    if (links.length === 0) return
    event.preventDefault()
    if (links.length === 1) {
      Turbo.visit(links[0].href)
    } else {
      // Multiple events: move focus to the first event link so Tab steps
      // through them in the grid's normal DOM order.
      links[0].focus()
    }
  }

  moveByDays(fromDate, delta) {
    const target = new Date(fromDate.getTime())
    target.setDate(target.getDate() + delta)
    this.focusDateOrCrossMonth(fromDate, target, delta)
  }

  moveToWeekEdge(fromDate, edge) {
    const target = new Date(fromDate.getTime())
    // JS: Sunday = 0, Monday = 1, ..., Saturday = 6. Monday-first index.
    const currentIdx = (target.getUTCDay() + 6) % 7
    const delta = edge === "start" ? -currentIdx : (6 - currentIdx)
    target.setDate(target.getDate() + delta)
    this.focusDate(target) || this.focusDateOrCrossMonth(fromDate, target, delta)
  }

  focusDateOrCrossMonth(fromDate, target, delta) {
    if (this.focusDate(target)) return
    const url = delta < 0 ? this.prevUrlValue : this.nextUrlValue
    this.visit(url)
  }

  focusDate(date) {
    const iso = date.toISOString().slice(0, 10)
    const cell = this.cellTargets.find(c => c.dataset.date === iso)
    if (!cell) return false
    this.cellTargets.forEach(c => c.setAttribute("tabindex", "-1"))
    cell.setAttribute("tabindex", "0")
    cell.focus()
    return true
  }

  parseDate(iso) {
    if (!iso) return null
    const [y, m, d] = iso.split("-").map(Number)
    if (!y || !m || !d) return null
    return new Date(Date.UTC(y, m - 1, d))
  }

  visit(url) {
    if (url) Turbo.visit(url)
  }
}
